import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { BusinessCardMock } from "../../components/BusinessCardMock";
import { toast } from "sonner";
import {
  CalendarDays, MessagesSquare, CreditCard, Images, Star, SlidersHorizontal,
  Handshake, Bell, LogOut, Copy, Trash2, Plus, Check, Loader2, Contact, Printer,
} from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800", confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-brand-ceruleanLight text-brand-cerulean", cancelled: "bg-red-100 text-red-700",
  new: "bg-amber-100 text-amber-800", reviewed: "bg-brand-ceruleanLight text-brand-cerulean",
  scheduled: "bg-emerald-100 text-emerald-800", closed: "bg-slate-100 text-slate-600",
};

const TABS = [
  { id: "bookings", label: "Appointments", icon: CalendarDays, testId: "admin-tab-bookings" },
  { id: "consultations", label: "Consultations", icon: MessagesSquare, testId: "admin-tab-consultations" },
  { id: "payment_links", label: "Payment Links", icon: CreditCard, testId: "admin-tab-payment-links" },
  { id: "portfolio", label: "Portfolio", icon: Images, testId: "admin-tab-portfolio" },
  { id: "testimonials", label: "Testimonials", icon: Star, testId: "admin-tab-testimonials" },
  { id: "availability", label: "Availability", icon: SlidersHorizontal, testId: "admin-tab-availability" },
  { id: "realtors", label: "Realtors", icon: Handshake, testId: "admin-tab-realtors" },
  { id: "notifications", label: "Notifications", icon: Bell, testId: "admin-tab-notifications" },
  { id: "business_card", label: "Business Card", icon: Contact, testId: "admin-tab-business-card" },
];

export default function AdminConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState({});

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-brand-sand flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 bg-brand-midnight text-white lg:min-h-screen shrink-0">
        <div className="p-5 border-b border-white/10"><Logo light /></div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.id} data-testid={t.testId} onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-brand-cerulean text-white" : "text-white/70 hover:bg-white/10"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 lg:mt-auto lg:absolute lg:bottom-0 lg:w-64 border-t border-white/10">
          <div className="px-4 py-2 text-xs text-white/50 truncate">{user?.email}</div>
          <button onClick={doLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 px-4 py-2 text-sm text-white/70 hover:text-white">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-5 lg:p-8 max-w-full overflow-hidden">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-8">
          {[
            ["Total Bookings", stats.bookings_total], ["Pending", stats.bookings_pending],
            ["Confirmed", stats.bookings_confirmed], ["Deposits Paid", stats.deposits_paid],
            ["Balance Due", stats.balance_pending], ["New Consults", stats.consultations_new],
          ].map(([l, v]) => (
            <div key={l} className="bg-white rounded-2xl border border-brand-border p-4">
              <div className="text-2xl font-serif font-semibold text-brand-cerulean">{v ?? 0}</div>
              <div className="text-xs text-brand-slate mt-0.5">{l}</div>
            </div>
          ))}
        </div>

        {tab === "bookings" && <Bookings onChange={loadStats} />}
        {tab === "consultations" && <Consultations onChange={loadStats} />}
        {tab === "payment_links" && <PaymentLinks />}
        {tab === "portfolio" && <Portfolio />}
        {tab === "testimonials" && <Testimonials />}
        {tab === "availability" && <Availability />}
        {tab === "realtors" && <Realtors />}
        {tab === "notifications" && <Notifications />}
        {tab === "business_card" && <BusinessCard owner={user} />}
      </main>
    </div>
  );
}

const Section = ({ title, subtitle, action, children }) => (
  <div>
    <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
      <div><h2 className="font-serif text-2xl text-brand-navy">{title}</h2>{subtitle && <p className="text-sm text-brand-slate">{subtitle}</p>}</div>
      {action}
    </div>
    {children}
  </div>
);

function Bookings({ onChange }) {
  const [rows, setRows] = useState([]);
  const [linkFor, setLinkFor] = useState(null);
  const load = useCallback(() => api.get("/admin/bookings").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success(`Marked ${status}`); load(); onChange();
  };

  return (
    <Section title="Appointments & Blocks" subtitle="Update status and send balance payment links.">
      <div className="bg-white rounded-2xl border border-brand-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-sand text-brand-slate text-left">
            <tr>{["Customer", "Service", "When", "Deposit", "Payment", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-brand-border">
                <td className="px-4 py-3"><div className="font-medium text-brand-navy">{b.name}</div><div className="text-xs text-brand-slate">{b.phone}</div></td>
                <td className="px-4 py-3"><div className="text-brand-navy">{b.service_name}</div><div className="text-xs text-brand-slate">{b.block}</div></td>
                <td className="px-4 py-3 whitespace-nowrap text-brand-slate">{b.date}<div className="text-xs">{b.time}</div></td>
                <td className="px-4 py-3">${b.deposit_amount}</td>
                <td className="px-4 py-3"><Badge className={b.payment_status === "deposit_paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{b.payment_status === "deposit_paid" ? "Deposit paid" : "Pending"}</Badge></td>
                <td className="px-4 py-3">
                  <select value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`}
                          className={`text-xs rounded-full px-3 py-1 border-0 font-medium ${STATUS_COLORS[b.status]}`}>
                    {["pending", "confirmed", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="outline" data-testid={`booking-balance-link-${b.id}`} onClick={() => setLinkFor(b)}
                          className="border-brand-cerulean text-brand-cerulean rounded-full text-xs">Balance link</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-brand-slate">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {linkFor && <BalanceLinkDialog booking={linkFor} onClose={() => setLinkFor(null)} />}
    </Section>
  );
}

function BalanceLinkDialog({ booking, onClose }) {
  const [amount, setAmount] = useState(String(booking.balance_amount || 0));
  const [description, setDescription] = useState("Remaining balance");
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);

  const create = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/payment-links", { booking_id: booking.id, amount: parseFloat(amount), description });
      setLink(data.url);
      toast.success("Payment link created");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="balance-link-dialog">
        <DialogHeader><DialogTitle className="font-serif">Balance Payment Link · {booking.name}</DialogTitle></DialogHeader>
        {!link ? (
          <div className="space-y-3">
            <div><Label>Amount (USD)</Label><Input data-testid="balance-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" /></div>
            <div><Label>Description</Label><Input data-testid="balance-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" /></div>
            <p className="text-xs text-brand-slate">Estimated total ${booking.estimated_total} · deposit ${booking.deposit_amount} already collected.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-brand-slate">Send this Stripe link to {booking.name}:</p>
            <div className="flex gap-2">
              <Input readOnly value={link} data-testid="balance-link-url" className="text-xs" />
              <Button data-testid="balance-copy-btn" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="bg-brand-cerulean text-white shrink-0"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
        <DialogFooter>
          {!link ? (
            <Button data-testid="balance-create-btn" onClick={create} disabled={loading} className="bg-brand-gold hover:bg-brand-goldHover text-white rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Link"}
            </Button>
          ) : <Button onClick={onClose} className="bg-brand-cerulean text-white rounded-full">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Consultations({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/consultations").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await api.patch(`/admin/consultations/${id}`, { status }); load(); onChange(); };

  return (
    <Section title="Consultations & Photo Submissions" subtitle="Review custom project requests and uploaded photos.">
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((c) => (
          <div key={c.id} data-testid={`consultation-card-${c.id}`} className="bg-white rounded-2xl border border-brand-border p-5">
            <div className="flex justify-between items-start">
              <div><div className="font-semibold text-brand-navy">{c.name}</div><div className="text-xs text-brand-slate">{c.email} · {c.phone}</div></div>
              <select value={c.status} onChange={(e) => setStatus(c.id, e.target.value)} data-testid={`consultation-status-${c.id}`}
                      className={`text-xs rounded-full px-3 py-1 border-0 font-medium ${STATUS_COLORS[c.status]}`}>
                {["new", "reviewed", "scheduled", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-brand-slate">
              <div><span className="text-brand-navy font-medium">Interest:</span> {c.service_interest}</div>
              <div><span className="text-brand-navy font-medium">Zip:</span> {c.zip_code || "—"}</div>
              <div><span className="text-brand-navy font-medium">Size:</span> {c.square_footage || "—"}</div>
              <div><span className="text-brand-navy font-medium">Target:</span> {c.target_date || "—"}</div>
            </div>
            {c.notes && <p className="mt-2 text-sm text-brand-slate bg-brand-sand rounded-lg p-2">{c.notes}</p>}
            {c.referral_source && <p className="mt-2 text-xs text-brand-gold">Realtor referral: {c.referral_source}</p>}
            {c.photos?.length > 0 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {c.photos.map((p, i) => (
                  <a key={i} href={`${API}/files/${p.path}`} target="_blank" rel="noreferrer" data-testid={`consultation-photo-${c.id}-${i}`}>
                    <img src={`${API}/files/${p.path}`} alt="project" className="w-16 h-16 object-cover rounded-lg border border-brand-border" />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-2 bg-white rounded-2xl border border-brand-border p-10 text-center text-brand-slate">No consultation requests yet.</div>}
      </div>
    </Section>
  );
}

function PaymentLinks() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/payment-links").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const markPaid = async (id) => { await api.patch(`/admin/payment-links/${id}`); toast.success("Marked paid"); load(); };

  return (
    <Section title="Stripe Balances & Payment Links" subtitle="Track remaining balances and generated links.">
      <div className="bg-white rounded-2xl border border-brand-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-sand text-brand-slate text-left"><tr>{["Customer", "Amount", "Description", "Link", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} data-testid={`payment-link-row-${l.id}`} className="border-t border-brand-border">
                <td className="px-4 py-3"><div className="font-medium text-brand-navy">{l.customer_name}</div><div className="text-xs text-brand-slate">{l.customer_email}</div></td>
                <td className="px-4 py-3 font-semibold text-brand-cerulean">${l.amount}</td>
                <td className="px-4 py-3 text-brand-slate">{l.description}</td>
                <td className="px-4 py-3"><button onClick={() => { navigator.clipboard.writeText(l.url); toast.success("Copied"); }} className="text-brand-cerulean flex items-center gap-1 text-xs"><Copy className="w-3 h-3" /> Copy</button></td>
                <td className="px-4 py-3"><Badge className={l.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{l.status}</Badge></td>
                <td className="px-4 py-3">{l.status !== "paid" && <Button size="sm" variant="outline" data-testid={`mark-paid-${l.id}`} onClick={() => markPaid(l.id)} className="text-xs rounded-full">Mark paid</Button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-slate">No payment links yet. Generate one from a booking.</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

const EMPTY_PORTFOLIO = { title: "", category: "Staging", image_url: "", description: "", published: true };
function Portfolio() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/portfolio").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (item) => {
    if (item.id) await api.put(`/admin/portfolio/${item.id}`, item);
    else await api.post("/admin/portfolio", item);
    toast.success("Saved"); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/admin/portfolio/${id}`); toast.success("Deleted"); load(); };

  return (
    <Section title="Portfolio Manager" subtitle="Add, edit, and remove showcase projects."
      action={<Button data-testid="admin-add-portfolio-btn" onClick={() => setEditing(EMPTY_PORTFOLIO)} className="bg-brand-cerulean text-white rounded-full"><Plus className="w-4 h-4 mr-1" /> Add Project</Button>}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} data-testid={`admin-portfolio-${p.id}`} className="bg-white rounded-2xl border border-brand-border overflow-hidden">
            <img src={p.image_url} alt={p.title} className="w-full h-36 object-cover" />
            <div className="p-4">
              <div className="text-xs text-brand-gold font-semibold">{p.category}</div>
              <div className="font-serif text-lg text-brand-navy">{p.title}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" data-testid={`edit-portfolio-${p.id}`} onClick={() => setEditing(p)} className="rounded-full text-xs flex-1">Edit</Button>
                <Button size="sm" variant="outline" data-testid={`delete-portfolio-${p.id}`} onClick={() => del(p.id)} className="rounded-full text-xs text-brand-coral border-brand-coral/40"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <PortfolioDialog item={editing} onClose={() => setEditing(null)} onSave={save} />}
    </Section>
  );
}

function PortfolioDialog({ item, onClose, onSave }) {
  const [form, setForm] = useState(item);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/portfolio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.image_url); toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="portfolio-dialog">
        <DialogHeader><DialogTitle className="font-serif">{item.id ? "Edit" : "Add"} Project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="portfolio-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" /></div>
          <div><Label>Category</Label>
            <select data-testid="portfolio-category-input" value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-input px-3 text-sm">
              {["Staging", "Closets", "Kitchen & Pantry", "Entire Home Moves", "Before/After"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><Label>Description</Label><Textarea data-testid="portfolio-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1" /></div>
          <div><Label>Image URL</Label><Input data-testid="portfolio-image-input" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="mt-1" placeholder="Paste URL or upload below" /></div>
          <label className="flex items-center gap-2 text-sm text-brand-cerulean cursor-pointer">
            <input type="file" accept="image/*" className="hidden" data-testid="portfolio-upload-input" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Upload image
          </label>
          {form.image_url && <img src={form.image_url} alt="preview" className="w-full h-32 object-cover rounded-lg" />}
          <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="portfolio-published-switch" /><span className="text-sm text-brand-slate">Published on site</span></div>
        </div>
        <DialogFooter>
          <Button data-testid="portfolio-save-btn" disabled={!form.title || !form.image_url} onClick={() => onSave(form)} className="bg-brand-cerulean text-white rounded-full">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_TESTIMONIAL = { name: "", location: "", quote: "", rating: 5, approved: true, featured: false };
function Testimonials() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/testimonials").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (item) => {
    if (item.id) await api.put(`/admin/testimonials/${item.id}`, item); else await api.post("/admin/testimonials", item);
    toast.success("Saved"); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); };

  return (
    <Section title="Testimonials Manager" subtitle="Approve, feature, and edit customer quotes."
      action={<Button data-testid="admin-add-testimonial-btn" onClick={() => setEditing(EMPTY_TESTIMONIAL)} className="bg-brand-cerulean text-white rounded-full"><Plus className="w-4 h-4 mr-1" /> Add</Button>}>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-white rounded-2xl border border-brand-border p-5">
            <div className="flex gap-0.5 mb-2">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-brand-gold text-brand-gold" />)}</div>
            <p className="text-brand-navy text-sm">"{t.quote}"</p>
            <div className="mt-3 flex justify-between items-end">
              <div><div className="font-semibold text-brand-navy text-sm">{t.name}</div><div className="text-xs text-brand-slate">{t.location}</div></div>
              <div className="flex gap-1">
                {t.approved && <Badge className="bg-emerald-100 text-emerald-800 text-xs">Approved</Badge>}
                {t.featured && <Badge className="bg-brand-goldSoft text-brand-goldHover text-xs">Featured</Badge>}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" data-testid={`edit-testimonial-${t.id}`} onClick={() => setEditing(t)} className="rounded-full text-xs flex-1">Edit</Button>
              <Button size="sm" variant="outline" data-testid={`delete-testimonial-${t.id}`} onClick={() => del(t.id)} className="rounded-full text-xs text-brand-coral border-brand-coral/40"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      {editing && <TestimonialDialog item={editing} onClose={() => setEditing(null)} onSave={save} />}
    </Section>
  );
}

function TestimonialDialog({ item, onClose, onSave }) {
  const [form, setForm] = useState(item);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="testimonial-dialog">
        <DialogHeader><DialogTitle className="font-serif">{item.id ? "Edit" : "Add"} Testimonial</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input data-testid="testimonial-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
            <div><Label>Location</Label><Input data-testid="testimonial-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label>Quote</Label><Textarea data-testid="testimonial-quote-input" value={form.quote} onChange={(e) => set("quote", e.target.value)} rows={3} className="mt-1" /></div>
          <div><Label>Rating</Label>
            <select data-testid="testimonial-rating-input" value={form.rating} onChange={(e) => set("rating", parseInt(e.target.value))} className="mt-1 w-full h-10 rounded-md border border-input px-3 text-sm">
              {[5, 4, 3].map((r) => <option key={r} value={r}>{r} stars</option>)}
            </select>
          </div>
          <div className="flex gap-6">
            <div className="flex items-center gap-2"><Switch checked={form.approved} onCheckedChange={(v) => set("approved", v)} data-testid="testimonial-approved-switch" /><span className="text-sm text-brand-slate">Approved</span></div>
            <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="testimonial-featured-switch" /><span className="text-sm text-brand-slate">Featured</span></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="testimonial-save-btn" disabled={!form.name || !form.quote} onClick={() => onSave(form)} className="bg-brand-cerulean text-white rounded-full">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Availability() {
  const [data, setData] = useState({ open_weekdays: [], blocked_dates: [] });
  const [newDate, setNewDate] = useState("");
  const load = useCallback(() => api.get("/admin/availability").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => { setData(next); await api.put("/admin/availability", { open_weekdays: next.open_weekdays, blocked_dates: next.blocked_dates }); toast.success("Availability updated"); };
  const toggleDay = (d) => { const set = new Set(data.open_weekdays); set.has(d) ? set.delete(d) : set.add(d); save({ ...data, open_weekdays: [...set].sort() }); };
  const addBlock = () => { if (newDate && !data.blocked_dates.includes(newDate)) { save({ ...data, blocked_dates: [...data.blocked_dates, newDate].sort() }); setNewDate(""); } };
  const rmBlock = (d) => save({ ...data, blocked_dates: data.blocked_dates.filter((x) => x !== d) });

  return (
    <Section title="Schedule & Emergency Blockouts" subtitle="Open or close booking days and block specific dates.">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-brand-border p-6">
          <h3 className="font-display font-semibold text-brand-navy mb-4">Open booking days</h3>
          <div className="space-y-3">
            {WEEKDAYS.map((w, i) => (
              <div key={w} className="flex items-center justify-between">
                <span className="text-sm text-brand-navy">{w}</span>
                <Switch data-testid={`availability-day-${i}`} checked={data.open_weekdays.includes(i)} onCheckedChange={() => toggleDay(i)} />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-brand-border p-6">
          <h3 className="font-display font-semibold text-brand-navy mb-4">Blocked dates</h3>
          <div className="flex gap-2 mb-4">
            <Input type="date" data-testid="availability-block-date-input" value={newDate} onChange={(e) => setNewDate(e.target.value)} min={new Date().toISOString().split("T")[0]} />
            <Button data-testid="admin-toggle-availability-btn" onClick={addBlock} className="bg-brand-cerulean text-white shrink-0"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.blocked_dates.map((d) => (
              <Badge key={d} data-testid={`blocked-date-${d}`} className="bg-red-100 text-red-700 gap-1.5 py-1">{d}<button onClick={() => rmBlock(d)}>×</button></Badge>
            ))}
            {data.blocked_dates.length === 0 && <span className="text-sm text-brand-slate">No blocked dates.</span>}
          </div>
        </div>
      </div>
    </Section>
  );
}

function Realtors() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/realtors").then((r) => setRows(r.data)); }, []);
  return (
    <Section title="Realtor Partner Requests" subtitle="Referral partners who signed up.">
      <div className="bg-white rounded-2xl border border-brand-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-sand text-brand-slate text-left"><tr>{["Name", "Brokerage", "Email", "Cell", "Listings/yr"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`realtor-row-${r.id}`} className="border-t border-brand-border">
                <td className="px-4 py-3 font-medium text-brand-navy">{r.name}</td><td className="px-4 py-3 text-brand-slate">{r.brokerage}</td>
                <td className="px-4 py-3 text-brand-slate">{r.email}</td><td className="px-4 py-3 text-brand-slate">{r.phone}</td><td className="px-4 py-3 text-brand-slate">{r.listings_per_year || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-slate">No realtor requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Notifications() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/notifications").then((r) => setRows(r.data)); }, []);
  return (
    <Section title="Notifications" subtitle="Confirmation & reminder emails (currently mocked until Gmail is connected).">
      <div className="mb-4 rounded-xl bg-brand-goldSoft border border-brand-gold/30 px-4 py-3 text-sm text-brand-goldHover">
        Email sending is <b>MOCKED</b> for v1. Messages are logged here. Connect Gmail / Google Workspace to send for real.
      </div>
      <div className="space-y-3">
        {rows.map((n) => (
          <div key={n.id} data-testid={`notification-${n.id}`} className="bg-white rounded-2xl border border-brand-border p-4">
            <div className="flex justify-between items-start"><div className="font-medium text-brand-navy">{n.subject}</div><Badge className="bg-slate-100 text-slate-600 text-xs">{n.type}</Badge></div>
            <div className="text-xs text-brand-slate mt-1">To: {n.to}</div>
            <p className="text-sm text-brand-slate mt-2">{n.body}</p>
          </div>
        ))}
        {rows.length === 0 && <div className="bg-white rounded-2xl border border-brand-border p-10 text-center text-brand-slate">No notifications yet.</div>}
      </div>
    </Section>
  );
}

function BusinessCard({ owner }) {
  const [card, setCard] = useState({
    name: "Dan Waters",
    title: "Founder & Lead Organizer",
    phone: "(850) 555-0142",
    email: "hello@pack2perfect.com",
    website: "pack2perfect.com",
    tagline: "From Moving Chaos to Turnkey Peace",
  });
  const set = (k, v) => setCard((c) => ({ ...c, [k]: v }));

  return (
    <Section title="Business Card Mockup" subtitle="A print-ready front & back card in the Pack 2 Perfection identity."
      action={<Button data-testid="business-card-print-btn" onClick={() => window.print()} className="bg-brand-cerulean text-white rounded-full"><Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF</Button>}>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
        <BusinessCardMock card={card} />

        {/* Editable fields */}
        <div className="bg-white rounded-2xl border border-brand-border p-5 no-print">
          <h3 className="font-display font-semibold text-brand-navy mb-4">Card details</h3>
          <div className="space-y-3">
            <div><Label>Owner name</Label><Input data-testid="card-name-input" value={card.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
            <div><Label>Title</Label><Input data-testid="card-title-input" value={card.title} onChange={(e) => set("title", e.target.value)} className="mt-1" /></div>
            <div><Label>Phone</Label><Input data-testid="card-phone-input" value={card.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="card-email-input" value={card.email} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
            <div><Label>Website</Label><Input data-testid="card-website-input" value={card.website} onChange={(e) => set("website", e.target.value)} className="mt-1" /></div>
            <div><Label>Tagline</Label><Textarea data-testid="card-tagline-input" value={card.tagline} onChange={(e) => set("tagline", e.target.value)} rows={2} className="mt-1" /></div>
          </div>
          <p className="text-xs text-brand-slate mt-4">Standard 3.5" × 2" card ratio. Use Print / Save PDF, then set margins to none and enable background graphics.</p>
        </div>
      </div>
    </Section>
  );
}

